import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Clock, ArrowLeft, Loader2, Tag, X, CheckCircle } from 'lucide-react';
import { format } from 'date-fns';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';
import Footer from './Footer';
import { useLot } from '../contexts/LotContext';
import { 
  Elements, 
  PaymentElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';

// Type declaration is handled globally - removing to avoid conflicts

// Helper function to get Stripe instance based on connected account
const getStripeInstance = (stripeAccountId?: string | null) => {
  if (stripeAccountId) {
    // For connected accounts, include the account ID
    return loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY, {
      stripeAccount: stripeAccountId
    });
  } else {
    // For platform payments, use regular initialization
    return loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);
  }
};

interface ParkingSession {
  id: string;
  license_plate: string;
  phone: string;
  hours: number;
  amount: number;
  payment_status: string;
  expires_at: string;
  auto_renew?: boolean;
  location: string;
  email?: string;
  first_name?: string;
  last_name?: string;
  scheduled_start_date?: string;
}

interface CheckoutFormProps {
  session: ParkingSession;
  sessionId: string;
  lotSlug: string;
  finalAmount: number;
}

// Separate checkout form component to be wrapped by Elements
function CheckoutForm({ session, sessionId, lotSlug, finalAmount }: CheckoutFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const [processing, setProcessing] = useState(false);

  const sendSmsNotification = async (session: ParkingSession) => {
    if (session.auto_renew) return;

    try {
      const functionUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-sms`;

      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          sessionId: session.id,
          phone: session.phone,
          licensePlate: session.license_plate,
          expiresAt: session.expires_at,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to send SMS notification');
      }
    } catch (error) {
      console.error('Error sending SMS:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements || !sessionId || !session) return;

    try {
      setProcessing(true);

      const { error: submitError, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/payment/complete/${sessionId}`,
        },
        redirect: 'if_required',
      });

      if (submitError) {
        throw new Error(submitError.message || 'Payment failed');
      }

      if (paymentIntent && paymentIntent.status === 'succeeded') {
        // The session is marked paid by the webhook's payment_intent.succeeded
        // handler, which runs with the service role. It used to be written from
        // here too, which required granting anon UPDATE on every column — and
        // that let anyone with the public key set payment_status themselves.
        // The confirmation page polls until the webhook lands.
        if (!session.auto_renew) {
          await sendSmsNotification(session);
        }

        toast.success('Payment successful!');
        navigate(`/lots/${lotSlug}/payment/complete/${sessionId}`);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error processing payment';
      toast.error(errorMessage);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <PaymentElement />
      
      <button
        type="submit"
        disabled={!stripe || !elements || processing}
        className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition duration-200 disabled:opacity-50"
      >
        {processing ? (
          <div className="flex items-center justify-center gap-2">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
            Processing...
          </div>
        ) : (
          `Pay $${finalAmount.toFixed(2)}${session.auto_renew ? '/month' : ''}`
        )}
      </button>
    </form>
  );
}

interface AppliedDiscount {
  promoCode: string;
  name: string;
  discountAmount: number;
}

export default function PaymentPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const { lot, isLoading, error } = useLot();
  const [session, setSession] = useState<ParkingSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [stripeAccountId, setStripeAccountId] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  const [fetchingPaymentIntent, setFetchingPaymentIntent] = useState(false);

  // Coupon state
  const [couponInput, setCouponInput] = useState('');
  const [validatingCoupon, setValidatingCoupon] = useState(false);
  const [appliedDiscount, setAppliedDiscount] = useState<AppliedDiscount | null>(null);
  const [isFreeSession, setIsFreeSession] = useState(false);
  const [confirmingFree, setConfirmingFree] = useState(false);

  // Must be memoized here (before any early returns) to satisfy rules of hooks
  const stripePromise = useMemo(() => getStripeInstance(stripeAccountId), [stripeAccountId]);

  useEffect(() => {
    fetchSession();
  }, [sessionId]);

  // Handle subscription redirects
  const handleSubscriptionPayment = async (sessionData: ParkingSession) => {
    try {
      setProcessing(true);
      
      if (!sessionData.email) {
        throw new Error('Email is required for monthly subscription payments');
      }
      
      // Create a subscription payment
      const subscriptionApiUrl = window.location.hostname === 'localhost' 
        ? '/api/create-subscription'  // Local development (proxied to Express server)
        : '/.netlify/functions/create-subscription'; // Production (Netlify functions)
        
      const response = await fetch(subscriptionApiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sessionId: sessionData.id,
          location: sessionData.location,
          email: sessionData.email,
          amount: sessionData.amount,
          scheduledStartDate: sessionData.scheduled_start_date,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create subscription');
      }

      const { sessionUrl, sessionId: stripeSessionId } = await response.json();
      
      // Update session with Stripe session ID
      await supabase
        .from('parking_sessions')
        .update({
          stripe_session_id: stripeSessionId
        })
        .eq('id', sessionData.id);
      
      // Redirect to Stripe Checkout
      window.location.href = sessionUrl;
    } catch (err) {
      setProcessing(false);
      const errorMessage = err instanceof Error ? err.message : 'Error setting up subscription';
      toast.error(errorMessage);
    }
  };

  const fetchSession = async () => {
    if (!sessionId) {
      toast.error('Invalid session ID');
      navigate(`/lots/${lot?.slug || 'adeline-street'}`);
      return;
    }

    // Check for subscription=true in URL parameters (returned from Stripe Checkout)
    const urlParams = new URLSearchParams(window.location.search);
    const isSubscriptionSuccess = urlParams.get('subscription') === 'true';

    if (isSubscriptionSuccess) {
      // If this is a redirect from successful subscription checkout
      toast.success('Subscription started successfully!');
      navigate(`/lots/${lot?.slug || 'adeline-street'}/payment/complete/${sessionId}`);
      return;
    }

    try {
      const { data: sessionData, error: sessionError } = await supabase
        .from('parking_sessions')
        .select('*')
        .eq('id', sessionId)
        .maybeSingle();

      if (sessionError) {
        throw sessionError;
      }

      if (!sessionData) {
        throw new Error('Session not found');
      }

      if (sessionData.payment_status === 'paid') {
        navigate(`/lots/${lot?.slug || 'adeline-street'}/payment/complete/${sessionId}`);
        return;
      }

      setSession(sessionData);
      
      // For subscriptions, redirect to Stripe Checkout
      if (sessionData.auto_renew) {
        await handleSubscriptionPayment(sessionData);
        return;
      }
      
      // For regular payments, create a payment intent (with protection against double calls)
      if (fetchingPaymentIntent) {
        console.log('Payment intent fetch already in progress, skipping...');
        return;
      }

      setFetchingPaymentIntent(true);
      
      const apiUrl = window.location.hostname === 'localhost' 
        ? '/api/create-payment-intent'  // Local development (proxied to Express server)
        : '/.netlify/functions/create-payment-intent'; // Production (Netlify functions)
        
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount: sessionData.amount,
          sessionId: sessionData.id,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create payment intent');
      }

      const { clientSecret, stripeAccountId } = await response.json();
      setClientSecret(clientSecret);
      setStripeAccountId(stripeAccountId || null);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load session';
      toast.error(errorMessage);
      navigate(`/lots/${lot?.slug || 'adeline-street'}`);
    } finally {
      setLoading(false);
      setFetchingPaymentIntent(false);
    }
  };

  const getApiUrl = (path: string) =>
    window.location.hostname === 'localhost'
      ? `/api/${path}`
      : `/.netlify/functions/${path}`;

  const finalAmount = session
    ? Math.max(0, session.amount - (appliedDiscount?.discountAmount ?? 0))
    : 0;

  const handleApplyCoupon = async () => {
    if (!couponInput.trim() || !lot || !session) return;

    setValidatingCoupon(true);
    try {
      const response = await fetch(getApiUrl('validate-coupon'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ promoCode: couponInput.trim(), lotSlug: lot.slug, stripeAccountId }),
      });

      const data = await response.json();

      if (!data.valid) {
        toast.error(data.error || 'Invalid coupon code');
        return;
      }

      // Calculate discount amount
      let discountAmount = 0;
      if (data.discount.amountOff !== null) {
        discountAmount = Math.min(data.discount.amountOff, session.amount);
      } else if (data.discount.percentOff !== null) {
        discountAmount = session.amount * (data.discount.percentOff / 100);
      }

      const newFinalAmount = Math.max(0, session.amount - discountAmount);

      setAppliedDiscount({
        promoCode: couponInput.trim().toUpperCase(),
        name: data.discount.name,
        discountAmount,
      });

      if (newFinalAmount === 0) {
        setIsFreeSession(true);
        setClientSecret(null);
        toast.success('Coupon applied — your parking is fully covered!');
        return;
      }

      // Re-create payment intent with coupon applied
      setIsFreeSession(false);
      const intentResponse = await fetch(getApiUrl('create-payment-intent'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: session.amount,
          sessionId: session.id,
          couponCode: couponInput.trim(),
          lotSlug: lot.slug,
          stripeAccountId,
        }),
      });

      if (!intentResponse.ok) {
        const errorData = await intentResponse.json();
        throw new Error(errorData.error || 'Failed to apply coupon');
      }

      const { clientSecret: newSecret, free, stripeAccountId: newStripeAccountId } = await intentResponse.json();

      if (free) {
        setIsFreeSession(true);
        setClientSecret(null);
      } else {
        setClientSecret(newSecret);
        setStripeAccountId(newStripeAccountId || null);
      }

      toast.success('Coupon applied!');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to apply coupon';
      toast.error(msg);
    } finally {
      setValidatingCoupon(false);
    }
  };

  const handleRemoveCoupon = async () => {
    if (!session) return;

    setAppliedDiscount(null);
    setIsFreeSession(false);
    setCouponInput('');

    // Re-create payment intent without coupon
    try {
      const response = await fetch(getApiUrl('create-payment-intent'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount: session.amount, sessionId: session.id, stripeAccountId }),
      });

      if (!response.ok) throw new Error('Failed to restore payment intent');
      const { clientSecret: newSecret, stripeAccountId: newStripeAccountId } = await response.json();
      setClientSecret(newSecret);
      setStripeAccountId(newStripeAccountId || null);
    } catch (err) {
      toast.error('Failed to remove coupon. Please refresh the page.');
    }
  };

  const handleConfirmFreeSession = async () => {
    if (!session || !appliedDiscount || !lot) return;

    setConfirmingFree(true);
    try {
      const response = await fetch(getApiUrl('confirm-free-session'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId: session.id,
          promoCode: appliedDiscount.promoCode,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to confirm free session');
      }

      toast.success('Parking confirmed — fully covered by your coupon!');
      navigate(`/lots/${lot.slug}/payment/complete/${sessionId}`);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to confirm session';
      toast.error(msg);
    } finally {
      setConfirmingFree(false);
    }
  };

  // Show loading state if lot data is still loading
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading parking lot...</p>
        </div>
      </div>
    );
  }

  // Show error state if lot data failed to load
  if (error || !lot) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="w-full max-w-md bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Unable to Load Parking Lot
            </h2>
            <p className="text-gray-600 mb-4">
              {error || 'Lot information not available'}
            </p>
            <Link
              to="/"
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Return to Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (loading || processing) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md bg-white rounded-xl shadow-lg p-6 text-center">
          <p className="text-red-600 mb-4">Session not found or payment setup failed</p>
          <Link
            to={`/lots/${lot?.slug || 'adeline-street'}`}
            className="text-blue-600 hover:text-blue-800 transition-colors duration-200"
          >
            Return to Home
          </Link>
        </div>
      </div>
    );
  }

  // If it's a subscription, we'll be redirected, so we just show a loading state
  if (session.auto_renew) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4 space-y-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <p className="text-gray-600">Setting up your subscription...</p>
      </div>
    );
  }

  // If it's not a subscription and we don't have a client secret, something went wrong
  // (unless the session is free via coupon — clientSecret is intentionally null in that case)
  if (!clientSecret && !isFreeSession) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md bg-white rounded-xl shadow-lg p-6 text-center">
          <p className="text-red-600 mb-4">Payment setup failed</p>
          <Link
            to="/"
            className="text-blue-600 hover:text-blue-800 transition-colors duration-200"
          >
            Return to Home
          </Link>
        </div>
      </div>
    );
  }

  const isBusinessHours = (() => {
    const now = new Date();
    // Use Intl to get the current day/hour in Eastern Time (handles EST/EDT automatically)
    const etParts = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York',
      weekday: 'short',
      hour: 'numeric',
      hour12: false,
    }).formatToParts(now);

    const weekday = etParts.find((p) => p.type === 'weekday')?.value;
    const hour = parseInt(etParts.find((p) => p.type === 'hour')?.value ?? '0', 10);

    const isWeekday = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].includes(weekday ?? '');
    const isDuringHours = hour >= 8 && hour < 17;

    return isWeekday && isDuringHours;
  })();

  const showCouponSection = !session.auto_renew && !!lot?.stripe_account_id && isBusinessHours;

  const options = {
    clientSecret: clientSecret!,
    appearance: {
      theme: 'stripe' as const,
    },
  };

  // stripePromise is memoized higher up in the component (before early returns)

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-xl shadow-lg p-6 space-y-6">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-900">Complete Payment</h1>
          <p className="mt-2 text-gray-600">Parking session details</p>
        </div>

        <div className="space-y-4">
          <div className="bg-gray-50 p-4 rounded-lg space-y-2">
            <p><strong>License Plate:</strong> {session.license_plate}</p>
            <p><strong>Duration:</strong> {session.auto_renew ? 'Monthly' : `${session.hours} hours`}</p>
            <div className="flex items-center justify-between">
              <p><strong>Amount:</strong></p>
              <div className="text-right">
                {appliedDiscount ? (
                  <div className="space-y-0.5">
                    <p className="line-through text-gray-400 text-sm">${session.amount.toFixed(2)}</p>
                    <p className="font-semibold text-green-600">
                      ${finalAmount.toFixed(2)}{session.auto_renew ? '/month' : ''}
                    </p>
                  </div>
                ) : (
                  <p>${session.amount.toFixed(2)}{session.auto_renew ? '/month' : ''}</p>
                )}
              </div>
            </div>
            {appliedDiscount && (
              <div className="flex items-center justify-between text-sm text-green-700 bg-green-50 rounded px-2 py-1">
                <div className="flex items-center gap-1">
                  <Tag size={13} />
                  <span>{appliedDiscount.name} — saves ${appliedDiscount.discountAmount.toFixed(2)}</span>
                </div>
                <button
                  onClick={handleRemoveCoupon}
                  className="text-green-600 hover:text-green-800 transition-colors ml-2"
                  aria-label="Remove coupon"
                >
                  <X size={14} />
                </button>
              </div>
            )}
            <div className="flex items-center gap-2 text-gray-600">
              <Clock size={16} />
              <p>{session.auto_renew ? 'Renews' : 'Expires'}: {format(new Date(session.expires_at), 'PPp')}</p>
            </div>
          </div>

          {showCouponSection && !appliedDiscount && (
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Have a coupon code?
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={couponInput}
                  onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                  onKeyDown={(e) => e.key === 'Enter' && handleApplyCoupon()}
                  placeholder="Enter code"
                  className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 uppercase"
                  disabled={validatingCoupon}
                />
                <button
                  onClick={handleApplyCoupon}
                  disabled={!couponInput.trim() || validatingCoupon}
                  className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {validatingCoupon ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    'Apply'
                  )}
                </button>
              </div>
            </div>
          )}

          {isFreeSession ? (
            <div className="space-y-4">
              <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-lg p-4">
                <CheckCircle className="text-green-600 shrink-0" size={20} />
                <div>
                  <p className="text-sm font-medium text-green-800">Your parking is fully covered!</p>
                  <p className="text-xs text-green-600 mt-0.5">No payment required — courtesy of your coupon.</p>
                </div>
              </div>
              <button
                onClick={handleConfirmFreeSession}
                disabled={confirmingFree}
                className="w-full bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 transition duration-200 disabled:opacity-50"
              >
                {confirmingFree ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    Confirming...
                  </div>
                ) : (
                  'Confirm Free Parking'
                )}
              </button>
            </div>
          ) : clientSecret ? (
            <Elements key={clientSecret} stripe={stripePromise} options={options}>
              <CheckoutForm
                session={session}
                sessionId={sessionId as string}
                lotSlug={lot?.slug || ''}
                finalAmount={finalAmount}
              />
            </Elements>
          ) : null}
        </div>

        <Footer />
      </div>
    </div>
  );
}